import { useState, useEffect, useRef } from 'react';
import { useAuth } from 'contexts/AuthContext';
import {
  getQualificationSubmissions,
  getAssignedSubmissionIds,
  submitQualificationEvaluationAsDraft,
  updateQualificationEvaluation,
  getExistingJudgments,
  completeAllEvaluations,
} from 'services/qualificationEvaluatorService';
import { getPillarDisplayName } from 'constants/awardCategoryPillar';
import { useRoleInfo } from 'hooks/useRoleInfo';
import { filterSubmissionsForEvaluation } from 'utils/submissionSorting';
import { Submission } from 'types/evaluation';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export interface EvaluationData {
  submissionId: string;
  isRevision?: boolean;
  existingJudgmentId?: string;
  scores?: any;
  [key: string]: any;
}

export const useQualificationEvaluator = () => {
  const { profile, isAuthenticated, awardRoles } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const currentYear = new Date().getFullYear();
  const EARLIEST_YEAR = 2025;

  const [year, setYear] = useState<number>(currentYear);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [existingJudgments, setExistingJudgments] = useState<
    Record<string, any>
  >({});
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);
  const [currentSubmissionIndex, setCurrentSubmissionIndex] =
    useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isCompletingAll, setIsCompletingAll] = useState<boolean>(false);
  const [isRefreshingJudgments, setIsRefreshingJudgments] =
    useState<boolean>(false);

  // Ref to track scroll position
  const scrollPositionRef = useRef<number>(0);

  const {
    userPillar,
    userRoleType,
    validUserCategories,
    userIsPillarPIC,
    userIsCategoryJudge,
    userIsSuperAdmin,
  } = useRoleInfo();

  const userPillarDisplayName = userPillar
    ? getPillarDisplayName(userPillar)
    : null;

  // Determine access control based on role type
  const userHasAccess =
    userIsSuperAdmin || (userIsCategoryJudge && validUserCategories.length > 0);

  // View mode: 'overview' or 'evaluating'
  const [viewMode, setViewMode] = useState<'overview' | 'evaluating'>(
    'overview'
  );

  // Success message function that fades away
  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const fetchQualificationSubmissions = async (fetchYear: number = year) => {
    try {
      setLoading(true);
      setError(null);

      const result = await getQualificationSubmissions(fetchYear);
      let allSubmissions: Submission[] = result.data || [];

      // Fetch assigned submission IDs for JUDGE_CATEGORY users
      let assignedSubmissionIds: Set<string> | undefined;
      if (userIsCategoryJudge && awardRoles?.data?.length) {
        const roleDocumentIds = awardRoles.data
          .map((role: any) => role.documentId)
          .filter(Boolean);
        if (roleDocumentIds.length > 0) {
          assignedSubmissionIds =
            await getAssignedSubmissionIds(roleDocumentIds);
        }
      }

      // Apply role-based filtering for evaluation page
      allSubmissions = filterSubmissionsForEvaluation(
        allSubmissions,
        userRoleType,
        userPillarDisplayName,
        assignedSubmissionIds
      );

      // Get existing judgments for this judge
      const judgeEmail = profile?.email;
      let judgments: Record<string, any> = {};

      if (judgeEmail && allSubmissions.length > 0) {
        judgments = await getExistingJudgments(allSubmissions, judgeEmail);
      } else if (!judgeEmail) {
        console.warn('⚠️ No judge email found - skipping judgment fetching');
      }

      setSubmissions(allSubmissions);
      setExistingJudgments(judgments);

      if (allSubmissions.length === 0) {
        let errorMsg;
        if (judgeEmail && Object.keys(judgments).length > 0) {
          // Judge has completed all submissions
          errorMsg = userPillarDisplayName
            ? `All qualification submissions have been completed (excluding ${userPillarDisplayName} pillar due to conflict of interest)`
            : 'All qualification submissions have been completed';
        } else {
          // No submissions available at all
          errorMsg = userPillarDisplayName
            ? `No qualification submissions found (excluding ${userPillarDisplayName} pillar due to conflict of interest)`
            : 'No qualification submissions found';
        }

        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('❌ Error fetching qualification submissions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch qualification submissions on component mount
  useEffect(() => {
    if (userHasAccess) {
      fetchQualificationSubmissions(year);
    }
  }, [userHasAccess, year]);

  // Handler for selecting a submission to evaluate
  const handleSelectSubmission = (submission: Submission) => {
    const submissionIndex = submissions.findIndex(
      s => (s.documentId || s.id) === (submission.documentId || submission.id)
    );
    setSelectedSubmission(submission);
    setCurrentSubmissionIndex(submissionIndex >= 0 ? submissionIndex : 0);
    setViewMode('evaluating');
  };

  // Handler for going back to overview
  const handleBackToOverview = () => {
    setSelectedSubmission(null);
    setCurrentSubmissionIndex(0);
    setViewMode('overview');
    // Refresh data to show updated statuses
    fetchQualificationSubmissions(year);
  };

  // Handler for navigating between submissions in detailed view (with circular navigation)
  const handleNavigateToSubmission = (newIndex: number) => {
    if (submissions.length === 0) return;

    // Handle circular navigation
    let actualIndex = newIndex;
    if (newIndex < 0) {
      actualIndex = submissions.length - 1; // Go to last submission
    } else if (newIndex >= submissions.length) {
      actualIndex = 0; // Go to first submission
    }

    const newSubmission = submissions[actualIndex];
    setSelectedSubmission(newSubmission);
    setCurrentSubmissionIndex(actualIndex);
  };

  const handleEvaluationSubmit = async (evaluationData: EvaluationData) => {
    try {
      setError(null);

      // Prepare judge information from auth context
      const judgeInfo = {
        name: profile?.fullName || 'Unknown Judge',
        email: profile?.email || 'unknown@email.com',
      };

      // reCAPTCHA is not required for internal evaluations

      let result;

      if (evaluationData.isRevision && evaluationData.existingJudgmentId) {
        // Update existing evaluation
        result = await updateQualificationEvaluation(
          evaluationData.existingJudgmentId,
          evaluationData,
          judgeInfo,
          true // Always save as draft for now
        );
      } else {
        // Create new draft evaluation
        result = await submitQualificationEvaluationAsDraft(
          evaluationData,
          judgeInfo
        );
      }

      if (result.success) {
        // Show success message without auto-navigation
        const action = evaluationData.isRevision ? 'updated' : 'saved';

        showSuccessMessage(
          `✅ Evaluation ${action} as draft successfully! You can continue reviewing or navigate manually.`
        );

        // Smooth refresh without scroll jumping or UI flicker
        const refreshJudgmentsSmooth = async () => {
          try {
            // Store current scroll position
            scrollPositionRef.current = window.scrollY;
            setIsRefreshingJudgments(true);

            const judgeEmail = profile?.email;
            if (judgeEmail && submissions.length > 0) {
              // Update judgments in background
              const updatedJudgments = await getExistingJudgments(
                submissions,
                judgeEmail
              );

              // Store target scroll position before state update
              const targetScrollY = scrollPositionRef.current;

              // Check if the current submission's judgment actually changed
              const currentSubmissionId =
                selectedSubmission?.documentId || selectedSubmission?.id;
              const currentJudgment = currentSubmissionId
                ? existingJudgments[currentSubmissionId]
                : null;
              const newJudgment = currentSubmissionId
                ? updatedJudgments[currentSubmissionId]
                : null;

              // Only update state if judgments actually changed to prevent unnecessary re-renders
              const judgmentsChanged =
                JSON.stringify(currentJudgment) !== JSON.stringify(newJudgment);

              if (
                judgmentsChanged ||
                Object.keys(updatedJudgments).length !==
                  Object.keys(existingJudgments).length
              ) {
                // Update state only if there are actual changes
                setExistingJudgments(updatedJudgments);
              }

              setIsRefreshingJudgments(false);

              // Immediate scroll restoration - no delay
              if (Math.abs(window.scrollY - targetScrollY) > 5) {
                window.scrollTo(0, targetScrollY);
              }
            }
          } catch (err) {
            console.warn('Failed to refresh judgments:', err);
            setIsRefreshingJudgments(false);
          }
        };

        // Minimal delay for better UX
        setTimeout(refreshJudgmentsSmooth, 200);
      } else {
        throw new Error(
          result.error || 'Unknown error occurred during submission'
        );
      }
    } catch (err: any) {
      console.error('❌ Error submitting evaluation:', err);

      // Provide detailed error feedback
      let errorMessage = 'Failed to save evaluation';

      if (err.message.includes('No judging data found')) {
        errorMessage = `Category "${selectedSubmission?.category}" is not configured for judgment. Please contact system administrator.`;
      } else if (err.message.includes('Submission not found')) {
        errorMessage =
          'The submission could not be found. It may have been deleted or moved.';
      } else if (err.message.includes('No valid aspect scores')) {
        errorMessage =
          'The evaluation scores could not be mapped to the category rubric. Please try again.';
      } else if (err.message.includes('Failed to fetch submission')) {
        errorMessage =
          'Unable to fetch submission details. Please check your network connection.';
      } else {
        errorMessage = `${errorMessage}: ${err.message}`;
      }

      setError(errorMessage);

      // Auto-scroll to error message
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  // Handler for completing all draft evaluations
  const handleCompleteAll = async () => {
    try {
      setIsCompletingAll(true);
      setError(null);

      // Get all judgment IDs that are drafts
      const draftJudgmentIds = Object.values(existingJudgments)
        .filter(judgment => judgment.overall_summary === 'Draft')
        .map(judgment => judgment.documentId);

      if (draftJudgmentIds.length === 0) {
        showSuccessMessage('No draft evaluations to complete.');
        setIsCompletingAll(false);
        return;
      }

      const judgeInfo = {
        name: profile?.fullName || 'Unknown Judge',
        email: profile?.email || 'unknown@email.com',
      };

      const result = await completeAllEvaluations(draftJudgmentIds, judgeInfo);

      if (result.success) {
        showSuccessMessage(
          `✅ All ${result.successfulCount} evaluations completed successfully!`
        );

        // Refresh data to show updated statuses and then re-enable button
        setTimeout(async () => {
          try {
            await fetchQualificationSubmissions();
          } catch (refreshError) {
            console.error(
              '❌ Error refreshing data after completion:',
              refreshError
            );
          } finally {
            setIsCompletingAll(false);
          }
        }, 1500);
      } else {
        const errorMsg = `Failed to complete some evaluations. ${result.successfulCount}/${result.totalProcessed} completed successfully.`;
        console.error('❌ Completion errors:', result.errors);
        setError(errorMsg);
        setIsCompletingAll(false);
      }
    } catch (err: any) {
      console.error('❌ Error completing all evaluations:', err);
      setError(`Failed to complete evaluations: ${err.message}`);
      setIsCompletingAll(false);
    }
  };

  return {
    isAuthenticated,
    userHasAccess,
    userIsPillarPIC,
    loading,
    year,
    setYear,
    EARLIEST_YEAR,
    currentYear,
    successMessage,
    isRefreshingJudgments,
    viewMode,
    submissions,
    existingJudgments,
    handleSelectSubmission,
    handleCompleteAll,
    isCompletingAll,
    validUserCategories,
    error,
    fetchQualificationSubmissions,
    selectedSubmission,
    handleEvaluationSubmit,
    handleBackToOverview,
    currentSubmissionIndex,
    handleNavigateToSubmission,
  };
};
